/**
 * Permission Check Functions
 *
 * Centralized permission checks for the application.
 * All UI visibility, route guards, and API pre-checks should use these helpers
 * to ensure consistent authorization logic.
 */

import type { AuthUser } from "@/services/authService";
import type { Client } from "@/types/client";
import type { NexusModule } from "@/types/modules";

import { ROLES, isRoleAtLeast } from "./roles";

// =============================================================================
// SUPER ADMIN
// =============================================================================

/**
 * Super admin email - hardcoded for security.
 * Only this user has access to admin features.
 */
const SUPER_ADMIN_EMAIL = "dave@track15.com";

/**
 * Check if user is a super admin (platform owner).
 * This is a hardcoded check for the platform owner only.
 */
export function isSuperAdmin(user: AuthUser | null): boolean {
  if (!user?.email) return false;
  return user.email.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
}

// =============================================================================
// USER ROLE CHECKS
// =============================================================================

/**
 * Check if user has a specific role.
 * Supports single role or array of roles (any match = true).
 */
export function hasRole(
  user: AuthUser | null,
  role: string | string[],
): boolean {
  if (!user) return false;

  const userRole = user.role;
  if (!userRole) return false;

  if (Array.isArray(role)) {
    return role.includes(userRole);
  }

  return userRole === role;
}

/**
 * Check if user is an admin.
 */
export function isAdmin(user: AuthUser | null): boolean {
  return hasRole(user, ROLES.ADMIN);
}

/**
 * Check if user is an owner (highest privilege level).
 */
export function isOwner(user: AuthUser | null): boolean {
  return hasRole(user, ROLES.OWNER);
}

/**
 * Check if user has at least manager-level access.
 */
export function isManager(user: AuthUser | null): boolean {
  if (!user?.role) return false;
  return isRoleAtLeast(user.role, ROLES.MANAGER);
}

// =============================================================================
// VIEW PERMISSIONS
// =============================================================================

/**
 * Check if user can view a client's data.
 * All authenticated users can view clients they have access to.
 */
export function canViewClient(
  user: AuthUser | null,
  _client: Client | null,
): boolean {
  // For now, any authenticated user can view any client
  // Future: check client membership
  return !!user;
}

/**
 * Check if user can view the admin section.
 */
export function canViewAdmin(user: AuthUser | null): boolean {
  return isAdmin(user) || isOwner(user);
}

/**
 * Check if user can view Brand Hub for a client.
 */
export function canViewBrandHub(
  user: AuthUser | null,
  client: Client | null,
): boolean {
  return canViewClient(user, client);
}

/**
 * Check if user can view Donor Lab for a client.
 * Donor Lab is part of the "core" module which is always enabled.
 */
export function canViewDonorLab(
  user: AuthUser | null,
  client: Client | null,
): boolean {
  if (!canViewClient(user, client)) return false;
  if (!client) return false;

  // Donor Lab is part of the core module, which is always enabled
  // Any authenticated user with a valid client can access Donor Lab
  return true;
}

// =============================================================================
// EDIT PERMISSIONS
// =============================================================================

/**
 * Check if user can edit a specific client's settings.
 *
 * NOTE: this is a conscious architectural hold, not a forgotten TODO.
 * `user.memberships` carries per-client roles (admin | member | viewer)
 * and the obvious extension is to grant edit access to a non-global-admin
 * who is `admin` or `member` on this specific client. We deliberately do
 * NOT do that yet because:
 *   - all current users are global admin (Dave is super-admin; Track15
 *     team members hold owner/admin DB roles), so the per-client path
 *     has never been exercised
 *   - granting edit-by-membership without a threat model + test
 *     harness is a security-shaped quick fix
 * Promote to a real per-client role gate (with vitest coverage for
 * member/admin/owner separation against the 5 Tier-U callers:
 * BrandHubPage, MessagingStrategyPage, NormalizationPage,
 * MajorDonorJourneyPage, DonorDataView) when the first non-admin user
 * attempts to use a Tier-U edit surface and surfaces a real "I should
 * be able to edit this" UX gap. See docs/temptation_log.md
 * "Per-client membership-role edit gating".
 */
export function canEditClient(
  user: AuthUser | null,
  client: Client | null,
): boolean {
  if (!user || !client) return false;
  if (isAdmin(user) || isOwner(user)) return true;
  return false;
}

/**
 * Check if user can edit brand settings for a client.
 */
export function canEditBrand(
  user: AuthUser | null,
  client: Client | null,
): boolean {
  return canEditClient(user, client);
}

/**
 * Check if user can import data for a client.
 */
export function canImportData(
  user: AuthUser | null,
  client: Client | null,
): boolean {
  return canEditClient(user, client);
}

/**
 * Check if user can manage users.
 */
export function canManageUsers(user: AuthUser | null): boolean {
  return isAdmin(user) || isOwner(user);
}

// =============================================================================
// ADMINISTRATIVE PERMISSIONS
// =============================================================================

/**
 * Check if user can access admin section.
 */
export function canAccessAdmin(user: AuthUser | null): boolean {
  return isAdmin(user) || isOwner(user);
}

/**
 * Check if user can create new clients.
 */
export function canCreateClient(user: AuthUser | null): boolean {
  return isAdmin(user) || isOwner(user);
}

/**
 * Check if user can delete a client.
 * More restrictive than edit - requires owner or admin.
 */
export function canDeleteClient(
  user: AuthUser | null,
  _client: Client | null,
): boolean {
  return isAdmin(user) || isOwner(user);
}

// =============================================================================
// MODULE PERMISSIONS
// =============================================================================

/**
 * Check if a module is enabled for a client.
 */
export function isModuleEnabled(
  client: Client | null,
  module: NexusModule,
): boolean {
  if (!client) return false;
  return client.enabledModules?.includes(module) ?? false;
}

/**
 * Check if Donor Lab is accessible for the current context.
 */
export function canAccessDonorLab(
  user: AuthUser | null,
  client: Client | null,
): boolean {
  return canViewDonorLab(user, client);
}

/**
 * Get the reason why Donor Lab is not accessible.
 */
export function getDonorLabAccessReason(
  user: AuthUser | null,
  client: Client | null,
): string | null {
  if (!user) return "Please sign in to access Donor Lab";
  if (!client) return "Select a client to access Donor Lab";
  if (!canAccessDonorLab(user, client)) {
    return "Donor Lab is not enabled for this client";
  }
  return null;
}

// =============================================================================
// WORKSPACE PERMISSIONS (Legacy compatibility)
// =============================================================================

/**
 * Check if user can access workspace settings.
 */
export function canAccessWorkspaceSettings(user: AuthUser | null): boolean {
  return !!user;
}

/**
 * Check if user can access admin section in settings.
 */
export function canAccessAdminSettings(user: AuthUser | null): boolean {
  return canAccessAdmin(user);
}

// =============================================================================
// PERMISSION SUMMARY
// =============================================================================

/**
 * Get a summary of user permissions for debugging.
 */
export function getPermissionSummary(
  user: AuthUser | null,
  client: Client | null,
): Record<string, boolean> {
  return {
    isAuthenticated: !!user,
    isAdmin: isAdmin(user),
    isOwner: isOwner(user),
    canViewAdmin: canViewAdmin(user),
    canAccessAdmin: canAccessAdmin(user),
    canCreateClient: canCreateClient(user),
    canEditClient: canEditClient(user, client),
    canViewBrandHub: canViewBrandHub(user, client),
    canEditBrand: canEditBrand(user, client),
    canAccessDonorLab: canAccessDonorLab(user, client),
    canImportData: canImportData(user, client),
    canManageUsers: canManageUsers(user),
  };
}
